import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SchedulerAPI } from "../infrastructure/api/Scheduler.api";
import {
    AvailabilityWindowInput,
    BookingSessionType,
    IntegrationProvider,
    ScheduleSessionInput,
    SchedulerSettingsUpdate,
    TherapistSpaceInput,
    UpcomingSubjectFilter,
} from "../domain/types";
import { subjectPayload } from "../domain/subject";

export const SETTINGS_KEY = "scheduler-settings";
export const AVAILABILITY_KEY = "scheduler-availability";
export const SPACES_KEY = "scheduler-spaces";
export const SLOTS_KEY = "scheduler-slots";
export const SESSIONS_KEY = "scheduler-sessions";
export const UPCOMING_KEY = "scheduler-upcoming";

export const useSchedulerSettings = () =>
    useQuery({
        queryKey: [SETTINGS_KEY],
        queryFn: async () => {
            const { data } = await SchedulerAPI.getSettings();
            return data;
        },
    });

export const useUpdateSchedulerSettings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: SchedulerSettingsUpdate) =>
            SchedulerAPI.updateSettings(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] });
            // Slots are derived from the timezone and duration.
            queryClient.invalidateQueries({ queryKey: [SLOTS_KEY] });
        },
    });
};

export const useAvailability = () =>
    useQuery({
        queryKey: [AVAILABILITY_KEY],
        queryFn: async () => {
            const { data } = await SchedulerAPI.getAvailability();
            return data;
        },
    });

export const useReplaceAvailability = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (windows: AvailabilityWindowInput[]) =>
            SchedulerAPI.replaceAvailability(windows),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [AVAILABILITY_KEY] });
            queryClient.invalidateQueries({ queryKey: [SLOTS_KEY] });
        },
    });
};

/**
 * `date` is `YYYY-MM-DD`; pass an empty string to hold the query. The slots
 * are as long as a session of `sessionType`.
 */
export const useSlots = (date: string, sessionType: BookingSessionType = "individual") =>
    useQuery({
        queryKey: [SLOTS_KEY, date, sessionType],
        queryFn: async () => {
            const { data } = await SchedulerAPI.getSlots(date, sessionType);
            return data;
        },
        enabled: Boolean(date),
    });

export const useSpaces = () =>
    useQuery({
        queryKey: [SPACES_KEY],
        queryFn: async () => {
            const { data } = await SchedulerAPI.listSpaces();
            return data;
        },
    });

export const useCreateSpace = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: TherapistSpaceInput) =>
            SchedulerAPI.createSpace(payload),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: [SPACES_KEY] }),
    });
};

export const useUpdateSpace = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            spaceId,
            payload,
        }: {
            spaceId: string;
            payload: TherapistSpaceInput;
        }) => SchedulerAPI.updateSpace(spaceId, payload),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: [SPACES_KEY] }),
    });
};

export const useDeleteSpace = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (spaceId: string) => SchedulerAPI.deleteSpace(spaceId),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: [SPACES_KEY] }),
    });
};

export const useDisconnectIntegration = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (provider: IntegrationProvider) =>
            SchedulerAPI.disconnectIntegration(provider),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] }),
    });
};

export const useConnectIntegration = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            provider,
            code,
            redirectUri,
        }: {
            provider: IntegrationProvider;
            code: string;
            redirectUri: string;
        }) => SchedulerAPI.connectIntegration(provider, code, redirectUri),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] }),
    });
};

/** `month` is `YYYY-MM`. */
export const useScheduledSessions = (month: string, includeCancelled = false) =>
    useQuery({
        queryKey: [SESSIONS_KEY, month, includeCancelled],
        queryFn: async () => {
            const { data } = await SchedulerAPI.listSessions(
                month,
                includeCancelled
            );
            return data;
        },
        enabled: Boolean(month),
    });

/**
 * The therapist's next sessions, optionally for one subject.
 *
 * The subject is part of the query key, so switching subjects re-queries
 * rather than reusing another subject's cached list. Narrowing is a request,
 * never a client-side trim of rows already held: the server bounds the page,
 * so trimming would silently cap the result at whatever the first page held.
 */
export const useUpcomingSessions = (
    subject: UpcomingSubjectFilter | null = null,
    enabled = true
) =>
    useQuery({
        queryKey: [UPCOMING_KEY, subject?.kind ?? null, subject?.id ?? null],
        queryFn: async () => {
            const { data } = await SchedulerAPI.listUpcoming(
                subject ? subjectPayload(subject) : {}
            );
            return data;
        },
        enabled,
    });

const invalidateBookings = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: [SESSIONS_KEY] });
    // A booking consumes a slot, so the slot list is stale immediately.
    queryClient.invalidateQueries({ queryKey: [SLOTS_KEY] });
    // Booking from one dock and then opening the other must not show a list
    // that disagrees with what was just created.
    queryClient.invalidateQueries({ queryKey: [UPCOMING_KEY] });
};

export const useScheduleSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: ScheduleSessionInput) =>
            SchedulerAPI.scheduleSession(payload),
        onSuccess: () => invalidateBookings(queryClient),
    });
};

export const useRescheduleSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            sessionId,
            start,
        }: {
            sessionId: string;
            start: string;
        }) => SchedulerAPI.rescheduleSession(sessionId, start),
        onSuccess: () => invalidateBookings(queryClient),
    });
};

export const useCancelSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: string | { sessionId: string; requestedByPatient: boolean }) =>
            typeof input === "string"
                ? SchedulerAPI.cancelSession(input)
                : SchedulerAPI.cancelSession(input.sessionId, input.requestedByPatient),
        onSuccess: () => invalidateBookings(queryClient),
    });
};
